import type { FlowSort, FlowView } from './view';
import { PLAYS_COLUMNS, buildPlaysDefaultFilters } from './plays-view';

/**
 * The Plays table's COLUMN contract, as the FlowTable mount needs it (plan
 * `plans/2026-09-11-004-plays-tab-qa-round2-dispatch.md` U17, amendment
 * **A11**).
 *
 * WHY A SEPARATE MODULE FROM `plays-view.ts`: `PLAYS_COLUMNS` there keys the
 * Type column `play_type_name` — the row FIELD its bespoke renderer read.
 * A `FlowTable` column key is not a renderer hint, it is the GROUP-BY and
 * SORT key the table hands back through `onGroupByChange`/`onSortChange`,
 * and the Plays filter catalog (`PLAYS_FILTER_FIELDS`) + the server's
 * `PLAYS_FIELD_MAP` both call that field `play_type`. Keeping two spellings
 * for one concept is what makes "group by Type" compile to a field the
 * server has never heard of, so the key is corrected HERE, once, and
 * `PLAYS_TABLE_COLUMNS` is what the mount consumes.
 *
 * Accepted consequence (dispatch open question 2): a view-state row
 * persisted before this rename still carries `play_type_name` in its
 * `columns` array, which no longer matches a column def — that rep loses the
 * Type column until they re-add it from the Columns popover. Uncommitted
 * work only; no migration.
 */
export const PLAYS_TABLE_COLUMNS: Array<{ key: string; label: string }> = PLAYS_COLUMNS.map((c) =>
  c.key === 'play_type_name' ? { key: 'play_type', label: c.label } : c,
);

/**
 * Default visible Plays columns over the CORRECTED keys — same rule as
 * `PLAYS_DEFAULT_COLUMNS` (every catalog entry, Subject and Body included),
 * re-derived here so a fresh rep's seeded column array can never contain the
 * retired `play_type_name` spelling.
 */
export const PLAYS_TABLE_DEFAULT_COLUMNS: string[] = PLAYS_TABLE_COLUMNS.map((c) => c.key);

/**
 * The Plays columns a rep can SORT on (A11's "sort on name/date/counts").
 *
 * Narrower than the column set on purpose: `status`/`city`/`target_persona`
 * and the two invite-copy columns are display-only here. Sorting happens
 * CLIENT-side (`orderPlaysForTable`) because `/api/flow/plays` returns the
 * whole list in one payload — there is no cursor to break — so an
 * un-orderable column is one this list has no comparator for, not one the
 * server refuses.
 */
export const PLAYS_SORTABLE_KEYS = ['name', 'start_date', 'capacity', 'suggested', 'invited_sent', 'responded'] as const;

/**
 * The Plays table's default sort. Matches the constant
 * `PLAYS_WORKING_STATE_REST` used to persist before sort was a real control,
 * so an existing `plays` view-state row restores to the order it already
 * showed rather than jumping on first load.
 */
export const PLAYS_DEFAULT_SORT: FlowSort = { field: 'start_date', dir: 'ASC' };

/**
 * Decodes a stored `workingState.sort` into a Plays sort.
 *
 * An absent row, or one whose `field` is not a `PLAYS_SORTABLE_KEYS` entry
 * (a stale tab persisting a retired key, or a `field` this table has no
 * comparator for) falls back to `PLAYS_DEFAULT_SORT` — never a phantom sort
 * that silently renders the payload order while the header claims otherwise.
 */
export function parsePlaysSort(stored: FlowSort | undefined): FlowSort {
  if (!stored) return PLAYS_DEFAULT_SORT;
  if (!(PLAYS_SORTABLE_KEYS as readonly string[]).includes(stored.field)) return PLAYS_DEFAULT_SORT;
  return { field: stored.field, dir: stored.dir === 'DESC' ? 'DESC' : 'ASC' };
}

/** Id of the single built-in Plays preset. */
export const PLAYS_DEFAULT_PRESET_ID = 'play_upcoming';

/** URL param carrying the active Plays saved-view id. */
export const PLAYS_VIEW_PARAM = 'view_play';

/**
 * The single built-in Plays preset, "Upcoming plays" (plan 2026-11-03-001, D1).
 *
 * WHY HERE, NOT `plays-view.ts`: the preset needs `PLAYS_SORTABLE`-era table
 * constants (`PLAYS_DEFAULT_SORT`, `PLAYS_TABLE_DEFAULT_COLUMNS`) that live in
 * this module, and this module already value-imports `plays-view.ts`.
 * `plays-view.ts` therefore must not value-import this file (import cycle),
 * so the preset's home is here.
 */
export const PLAYS_PRESETS: FlowView[] = [
  {
    id: PLAYS_DEFAULT_PRESET_ID,
    createdBy: null,
    entity: 'play',
    name: 'Upcoming plays',
    filter: buildPlaysDefaultFilters(),
    groupLogic: 'AND',
    sort: PLAYS_DEFAULT_SORT,
    groupBy: ['play_type'],
    columns: PLAYS_TABLE_DEFAULT_COLUMNS,
    isDefault: true,
  },
];
